import EditDocumentIcon from '@mui/icons-material/EditDocument';
import { Stack, Typography } from '@mui/material';
import { FormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import {
  FileField,
  required,
  type TransformData,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { FieldValues } from 'react-hook-form';

import { GetB2bCompanyPaymentMethodResponse } from '@Common/types/bo/b2b/companies';

import {
  ActionModalFormWithToolbar,
  DialogToolbar,
  EditInDialogButton,
  FileInput,
  SaveButton,
} from '@sh4res/bo-common/components';
import { useResourceBasePath, useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';

type Props = {
  paymentMethod: GetB2bCompanyPaymentMethodResponse[number];
};

export const B2bCompanyPaymentMethodUpdate = (props: Props) => {
  const { paymentMethod } = props;
  const resourceBasePath = useResourceBasePath();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const { upload } = useUploadPresignedUrl();

  const resource = `${resourceBasePath}companies/payment_methods` as Resources;

  const onSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.companies.payment_methods.update.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const transform: TransformData = useCallback(
    async (data: FieldValues) => {
      const { rib } = data;
      if (!rib?.rawFile || !(rib.rawFile instanceof File)) {
        throw new Error('RIB document is required');
      }
      const uploadResult = await upload(rib.rawFile);
      if (!uploadResult) {
        throw new Error('Error during upload');
      }
      return {
        presigned_path: uploadResult.path,
        document_name: rib.title || null,
      };
    },
    [upload],
  );

  return (
    <EditInDialogButton
      testId="BoExternalB2bCompanyPaymentMethodUpdate_UpdateButton"
      resource={resource}
      id={paymentMethod.id}
      fullWidth
      maxWidth="lg"
      title={`${translate('resources.bo_common.b2b.companies.payment_methods.update.title')}
         - ${translate(
           `resources.bo_common.b2b.companies.payment_methods.items.${paymentMethod.type}.${paymentMethod.intent}.title`,
         )}`}
      ButtonProps={{
        variant: 'text',
        sx: { backgroundColor: 'transparent', color: 'primary.main', minWidth: '32px' },
      }}
      icon={<EditDocumentIcon />}
      mutationMode="pessimistic"
      transform={transform}
      mutationOptions={{ onSuccess, onError }}>
      <Stack spacing={3} px={2} pt={3}>
        <Typography variant="body1" fontWeight={600}>
          {translate('resources.bo_common.b2b.companies.payment_methods.update.reason')}
        </Typography>
        <Typography variant="body1">{paymentMethod.retry_comment ?? '-'}</Typography>
        <Typography variant="body1" fontWeight={600}>
          {translate('resources.bo_common.b2b.companies.payment_methods.update.documents')}
        </Typography>
      </Stack>
      <ActionModalFormWithToolbar
        toolbar={
          <DialogToolbar
            formDialogContext={FormDialogContext}
            button={
              <SaveButton
                color="primary"
                permissions={{ resource, action: 'edit' }}
                testId="BoExternalB2bCompanyPaymentMethodUpdate_SaveButton"
              />
            }
          />
        }>
        <FileInput
          source="rib"
          label={translate('resources.bo_common.b2b.companies.payment_methods.update.subtitle')}
          accept=".pdf"
          isRequired
          validate={[required()]}
          data-testid="BoExternalB2bCompanyPaymentMethodUpdate_RibFileInput">
          <FileField
            source="src"
            title="title"
            data-testid="BoExternalB2bCompanyPaymentMethodUpdate_RibFileField"
          />
        </FileInput>
        <Typography variant="body1" color="textSecondary" pt={3}>
          {translate('resources.bo_common.b2b.companies.payment_methods.update.footnote')}
        </Typography>
      </ActionModalFormWithToolbar>
    </EditInDialogButton>
  );
};
